import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Search, X } from 'lucide-react';
import { API_URL } from '../lib/utils';
import { fetchCompaniesByIds } from '../lib/companies';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

/**
 * Async multi-select company picker.
 *
 * Designed for forms where a record can be linked to multiple companies
 * (e.g. a Student with `company_ids: []`). Avoids loading thousands of
 * companies up front — searches server-side as the user types.
 *
 * Props:
 *   value              - string[] currently-selected company ids
 *   onChange(ids)      - called with the new array of ids
 *   selectedCompanies  - optional prefetched [{id, name}, ...] for showing the
 *                        labels on initial render (avoids one extra request)
 *   placeholder        - search-input placeholder
 *   disabled           - disables interaction
 */
export const CompanyMultiPicker = ({
  value = [],
  onChange,
  selectedCompanies = [],
  placeholder = 'Search companies...',
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labelMap, setLabelMap] = useState(() => {
    const m = new Map();
    (selectedCompanies || []).forEach((c) => m.set(c.id, c.name));
    return m;
  });
  const debounceRef = useRef(null);

  // Merge in any new selectedCompanies passed by the parent
  useEffect(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return;
    setLabelMap((prev) => {
      const next = new Map(prev);
      selectedCompanies.forEach((c) => {
        if (c && c.id && !next.has(c.id)) next.set(c.id, c.name);
      });
      return next;
    });
  }, [selectedCompanies]);

  // For any selected id whose label we don't know yet, fetch it
  useEffect(() => {
    if (!value || value.length === 0) return;
    const missing = value.filter((id) => !labelMap.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchCompaniesByIds(missing);
        if (cancelled) return;
        setLabelMap((prev) => {
          const next = new Map(prev);
          list.forEach((c) => next.set(c.id, c.name));
          return next;
        });
      } catch (err) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (search.trim().length > 0) params.search = search.trim();
        const res = await axios.get(`${API_URL}/companies/lookup`, { params });
        const list = res.data?.items || [];
        setItems(list);
        // also cache labels for the results we got back
        setLabelMap((prev) => {
          const next = new Map(prev);
          list.forEach((c) => next.set(c.id, c.name));
          return next;
        });
      } catch (err) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, search.trim().length > 0 ? 250 : 0);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);

  const toggle = (id) => {
    if (disabled) return;
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const remove = (id) => {
    if (disabled) return;
    onChange(value.filter((x) => x !== id));
  };

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="company-multipicker-selected">
          {value.map((id) => (
            <Badge
              key={id}
              variant="secondary"
              className="cursor-pointer hover:bg-red-100"
              onClick={() => remove(id)}
              data-testid={`company-multipicker-chip-${id}`}
            >
              {labelMap.get(id) || id}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          disabled={disabled}
          data-testid="company-multipicker-search"
        />
      </div>

      {/* Results */}
      <div className="border rounded-lg max-h-[180px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching...
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            {search.trim() ? 'No companies match your search' : 'Start typing to search'}
          </div>
        ) : (
          items.map((c) => {
            const checked = value.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`w-full text-left flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 hover:bg-slate-100 ${
                  checked ? 'bg-emerald-50' : ''
                }`}
                data-testid={`company-multipicker-option-${c.id}`}
              >
                <span className="truncate">
                  {c.name}
                  {c.category && (
                    <span className="ml-2 text-xs text-slate-400">({c.category})</span>
                  )}
                </span>
                {checked && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CompanyMultiPicker;
